import { useEffect, useRef, useState } from 'react';
import type { SocialPostDraftCard } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { requestChatComposerFocus } from '../../../../lib/chat-composer-focus';
import { draftHeadline, draftSpaceSlug, type DraftPreviewCardData } from '../../../../lib/draft-preview';
import { useDraftState, type DraftStatePoll } from '../../../../lib/draft-preview-api';
import type { SendDraftComments } from '../../../../lib/draft-review-store';
import { draftSuperseded } from '../../../../lib/draft-state';
import { onChatTurnFinished } from '../../../../lib/site-edit-store';
import { socialPostLogoKey } from '../../../../lib/social-post-preview-card';
import { useWorkspaces } from '../../../../lib/workspaces';
import { Card, SocialLogo, Typography } from '../../ui';
import { DraftActions } from './DraftActions';
import { SocialPostPreviewCard } from './SocialPostPreviewCard';
import styles from './DraftPreviewCard.module.css';

interface SocialDraftPreviewCardProps {
  card: DraftPreviewCardData & { post: SocialPostDraftCard };
  conversationId?: string;
  latest?: boolean;
  canSend?: boolean;
  held?: boolean;
  sendComments?: SendDraftComments;
  biPage?: string;
  onEditFirst?: () => void;
  flush?: boolean;
}

export function SocialDraftPreviewCard({
  card,
  conversationId,
  latest = true,
  canSend = true,
  held = false,
  sendComments,
  biPage = 'chat',
  onEditFirst = requestChatComposerFocus,
  flush = false,
}: SocialDraftPreviewCardProps) {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const spaceSlug = workspaces.status === 'ready' ? draftSpaceSlug(card, workspaces.workspaces) : card.spaceSlug;
  const headline = draftHeadline(card);
  const ids = { workspaceId: card.workspaceId, taskId: card.taskId, draftId: card.draftId, kind: card.kind };
  const [poll, setPoll] = useState<DraftStatePoll | null>(null);
  const taskState = useDraftState(ids, latest, poll);
  const stateRef = useRef(taskState.data);
  stateRef.current = taskState.data;
  useEffect(() => {
    if (!latest || !conversationId) return undefined;
    return onChatTurnFinished((id) => {
      const state = stateRef.current;
      const done = !!state?.delivered && !state.delivered.scheduled;
      if (id !== conversationId || state?.ended || done) return;
      setPoll({ at: Date.now(), deliveredAtTap: state?.delivered ?? null, scheduledAtTap: state?.scheduled?.at ?? null });
    });
  }, [latest, conversationId]);
  const superseded = draftSuperseded(taskState.data, card.post.updatedAt);
  const wentOut = !!taskState.data?.delivered && !taskState.data.delivered.scheduled;

  return (
    <Card
      variant="sunken"
      size="sm"
      disablePadding={flush}
      className={flush ? `${styles.card} ${styles.flush}` : styles.card}
      data-testid="chat-draft-preview-card"
      data-kind={card.kind}
    >
      <div className={styles.row}>
        <span className={styles.glyph} aria-hidden>
          <SocialLogo network={socialPostLogoKey(card.post.network)} size={18} />
        </span>
        <span className={styles.text}>
          <Typography variant="p3" color="secondary">
            {t(wentOut ? 'socialDraft.card.titleDone' : 'socialDraft.card.title')}
          </Typography>
          {headline && (
            <Typography variant="p2" color="primary" className={styles.title}>
              {headline}
            </Typography>
          )}
        </span>
      </div>
      {latest && (
        <div className={styles.previewTap} data-testid="socialDraftPreview">
          <SocialPostPreviewCard card={card.post} />
        </div>
      )}
      {latest && !held && !superseded && (
        <DraftActions
          card={card}
          state={taskState.data}
          spaceSlug={spaceSlug}
          canSend={canSend}
          sendComments={sendComments}
          biPage={biPage}
          onEditFirst={onEditFirst}
          onSent={() => setPoll({ at: Date.now(), deliveredAtTap: taskState.data?.delivered ?? null, scheduledAtTap: taskState.data?.scheduled?.at ?? null })}
        />
      )}
    </Card>
  );
}
